<script lang="ts">
export default {
  name: 'PolicyDocumentUploadListModal',
};
</script>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import AppModal from '@/components/v2/AppModal.vue';
import clientReadinessService, { ClientReadinessPolicy } from '@/services/v2/ClientReadinessService';
import UploadPolicyDocumentModal from '@/components/v2/UploadPolicyDocumentModal.vue';
import { documentService } from '@/services/v2/DocumentService';
import { Commit } from '@/store';
import { StoreMutationType } from '@/types/StoreMutationType';
import { UploadDocumentRequest, DocumentType, UploadDocumentResponse } from '@/types/v2/Document';

const props = withDefaults(defineProps<{
  accountId: number;
  ShowNonCAC: boolean;
  ShowCurrentOnly: boolean;
  docType?: string;
  title?: string;
}>(), {
  docType: "Policy",
  title: "Upload Policy Documents",
});

const emit = defineEmits<{
  (e: 'document-uploaded'): void;
  (e: 'close'): void;
}>();

// refs
// -----
const policies = ref<ClientReadinessPolicy[]>();
const selectedPolicy = ref<ClientReadinessPolicy | null>(null);
const showUploadDocumentModal = ref(false);
const policyDocumentTypes = ref<DocumentType[]>([
]);

// methods
// --------
async function attachFilesToPolicy(uploadRequests: Partial<UploadDocumentRequest>[]): Promise<UploadDocumentResponse> {
  if (!selectedPolicy.value) {
    return { success: false, messages: ['No policy selected'] };
  }

  const policyID = selectedPolicy.value.policyId;

  try {
    const uploadDocumentPromises = uploadRequests.map((request) =>
      documentService.uploadDocumentForPolicyOrAccount({
        ...request,
        accountId: props.accountId,
        policyId: policyID,
      } as UploadDocumentRequest),
    );

    await Promise.all(uploadDocumentPromises)
      .then(async () => {
        Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Document attached successfully!' });
        showUploadDocumentModal.value = false;
        selectedPolicy.value = null;
        await refresh();
        emit('document-uploaded');
      })
      .catch((e) => {
        console.error(e);
        Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Failed to attach document', color: 'error' });
        showUploadDocumentModal.value = false;
        selectedPolicy.value = null;
        return { success: false, messages: ['Failed to attach document'] };
      });
  } catch (e) {
    console.error(e);
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Failed to attach document', color: 'error' });
    showUploadDocumentModal.value = false;
    selectedPolicy.value = null;
    return { success: false, messages: ['Failed to attach document'] };
  }

  return { success: true, messages: [] };
}

function openDocumentUploadModal(policy: ClientReadinessPolicy) {
  selectedPolicy.value = policy;
  showUploadDocumentModal.value = true;
}

async function refresh() {
  //   fetch policies without documents
  policies.value = await clientReadinessService.getPoliciesWithoutDocuments(props.accountId, {
    showNonCAC: props.ShowNonCAC,
    showCurrentOnly: props.ShowCurrentOnly,
    documentType: props.docType,
  });
}

// lifecycles
// -----------
onMounted(async () => {
  policyDocumentTypes.value = (await documentService.getDocumentTypes()).filter((type) => type.name === (props.docType));
  await refresh();
});
</script>

<template>
  <AppModal :show="true" action-button-label="" :loading="false" modalClass="tw:max-w-[700px]">
    <template v-slot:title>
      <h2 class="tw:font-normal tw:text-red-500">{{ title }}</h2>
    </template>
    <template v-slot:content>
      <!--      list of policies with upload icon inline -->
      <div class="tw:mx-2 tw:flex tw:max-h-[40vh] tw:flex-col tw:overflow-y-auto">
        <div
          v-for="policy in policies"
          :key="policy.policyId"
          class="tw:group tw:mb-2 tw:flex tw:w-full tw:items-center tw:justify-between tw:gap-5 tw:rounded-lg tw:p-2 tw:hover:bg-base-200"
        >
          <div class="tw:flex tw:truncate tw:text-sm">
            <span class="tw:mr-1 tw:font-bold">{{ policy.mainPolicyNumber }}</span
            ><span>{{ ' - ' + policy.description }}</span>
          </div>
          <div>
            <button
              @click="openDocumentUploadModal(policy)"
              class="tw:btn tw:btn-xs tw:flex tw:opacity-40 tw:group-hover:btn-primary tw:group-hover:text-white tw:group-hover:opacity-100"
            >
              Upload
            </button>
          </div>
        </div>
      </div>
    </template>
    <template v-slot:actions>
      <button class="tw:btn tw:btn-primary tw:btn-sm tw:text-white" @click="() => $emit('close')">Done</button>
    </template>
  </AppModal>

  <UploadPolicyDocumentModal
    v-if="showUploadDocumentModal && selectedPolicy"
    title="Upload Document"
    :description="'Select document for Policy ' + selectedPolicy.mainPolicyNumber"
    :upload="(documents) => attachFilesToPolicy(documents)"
    :policy-document-types="policyDocumentTypes"
    accepted-content-types=""
    :is-user-allowed="true"
    @close="
      selectedPolicy = null;
      showUploadDocumentModal = false;
    "
  >
  </UploadPolicyDocumentModal>
</template>
